import { Effect } from 'effect';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { sessionPromptEffect, sessionStreamEffect } from '../client';
import { useRequestOwner } from './use-request-owner';
import type {
  CompletionEvent,
  CompletionOptions,
  GenerateOptions,
  GenerateResult,
  OkraSession,
} from '../types';
import { okraHookFailure, runOkraHookEffect, type OkraHookError } from './hook-io';
import type { Message, UseChatReturn, ChatConfig, ChatStatus } from './types';

let _idCounter = 0;
function genId(): string {
  return `msg_${Date.now()}_${++_idCounter}`;
}

/**
 * Native non-streaming chat turn — the IO seam behind {@link useChat}.
 *
 * The question, the answer, and the cited snippets stay out of the failure
 * payload; only the operation label travels with the tagged error while the
 * original rejection is restored for the hook's abort check.
 */
export function sendChatPromptEffect(
  session: OkraSession,
  query: string,
  options: Record<string, unknown> = {},
): Effect.Effect<GenerateResult, OkraHookError> {
  return sessionPromptEffect<undefined>(session, query, options as GenerateOptions).pipe(
    Effect.mapError((error) =>
      okraHookFailure('chatPrompt', 'Chat completion failed', error.cause),
    ),
  );
}

export function streamChatPromptEffect(
  session: OkraSession,
  query: string,
  options: CompletionOptions,
  onEvent: (event: CompletionEvent) => void,
): Effect.Effect<void, OkraHookError> {
  return sessionStreamEffect(session, query, options, onEvent).pipe(
    Effect.mapError((error) =>
      okraHookFailure('chatPrompt', 'Chat completion failed', error.cause),
    ),
  );
}

export function useChat<TMessage = Message>(config: ChatConfig<TMessage>): UseChatReturn<TMessage> {
  const { session, stream = true, completionOptions, adapter, onFinish, onError } = config;
  const [okraMessages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const owner = useRequestOwner(session, true);
  const [turn, setTurn] = useState<{ owner: object; isLoading: boolean; status: ChatStatus }>({
    owner,
    isLoading: false,
    status: 'idle',
  });
  const current = turn.owner === owner ? turn : { isLoading: false, status: 'idle' as const };
  const isLoading = current.isLoading;
  const status = current.status;

  useEffect(() => {
    setTurn({ owner, isLoading: false, status: 'idle' });
  }, [owner]);
  const stop = useCallback(() => {
    if (owner.cancel()) setTurn({ owner, isLoading: false, status: 'idle' });
  }, [owner]);

  const append = useCallback((msg: Pick<Message, 'role' | 'content'>) => {
    const full: Message = { id: genId(), createdAt: new Date(), ...msg };
    setMessages((prev) => [...prev, full]);
  }, []);

  const sendMessages = useCallback(
    async (userQuery: string) => {
      if (!session) return;
      const controller = owner.begin();
      if (!controller) return;
      const isCurrent = () => owner.owns(controller);
      setMessages((prev) => [
        ...prev,
        { id: genId(), role: 'user', content: userQuery, createdAt: new Date() },
      ]);
      setInput('');
      setTurn({ owner, isLoading: true, status: 'submitted' });
      let didError = false;
      try {
        if (stream) {
          const assistantId = genId();
          let fullText = '';
          let sources: Array<{ page: number; snippet: string }> | undefined;
          setMessages((prev) => [
            ...prev,
            { id: assistantId, role: 'assistant', content: '', createdAt: new Date() },
          ]);
          await runOkraHookEffect(
            streamChatPromptEffect(session, userQuery, completionOptions ?? {}, (event) => {
              if (!isCurrent()) return;
              if (event.type === 'text_delta') {
                fullText += event.text;
                const content = fullText;
                setTurn({ owner, isLoading: true, status: 'streaming' });
                setMessages((prev) =>
                  prev.map((message) =>
                    message.id === assistantId ? { ...message, content } : message,
                  ),
                );
              } else if (event.type === 'done') {
                fullText = event.answer || fullText;
                sources = event.sources;
              } else if (event.type === 'error') {
                throw new Error(event.message);
              }
            }),
            controller.signal,
          );
          if (!isCurrent()) return;
          const finalMsg: Message = {
            id: assistantId,
            role: 'assistant',
            content: fullText,
            createdAt: new Date(),
            sources,
          };
          setMessages((prev) =>
            prev.map((message) => (message.id === assistantId ? finalMsg : message)),
          );
          onFinish?.(finalMsg);
        } else {
          const result = await runOkraHookEffect(
            sendChatPromptEffect(session, userQuery, completionOptions),
            controller.signal,
          );
          if (!isCurrent()) return;
          const assistantMsg: Message = {
            id: genId(),
            role: 'assistant',
            content: result.answer,
            createdAt: new Date(),
            sources: result.sources,
          };
          setMessages((prev) => [...prev, assistantMsg]);
          onFinish?.(assistantMsg);
        }
      } catch (cause) {
        if (!isCurrent()) return;
        didError = true;
        setTurn({ owner, isLoading: true, status: 'error' });
        onError?.(cause instanceof Error ? cause : new Error(String(cause)));
      } finally {
        if (isCurrent()) setTurn({ owner, isLoading: false, status: didError ? 'error' : 'idle' });
      }
    },
    [session, owner, stream, completionOptions, onFinish, onError],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setInput(e.target.value);
    },
    [],
  );
  const handleSubmit = useCallback(
    (e?: { preventDefault?: () => void }) => {
      e?.preventDefault?.();
      const trimmed = input.trim();
      if (!trimmed || isLoading || !session) return;
      void sendMessages(trimmed);
    },
    [input, isLoading, session, sendMessages],
  );

  const messages = useMemo<TMessage[]>(() => {
    if (!adapter) return okraMessages as unknown as TMessage[];
    return okraMessages.map((message) => adapter.toMessage(message));
  }, [adapter, okraMessages]);

  return {
    messages,
    okraMessages,
    input,
    handleInputChange,
    handleSubmit,
    isLoading,
    status,
    stop,
    append,
    setMessages,
  };
}
